import { QuestionCircleOutlined } from '@ant-design/icons'
import { MenuDataItem, ProLayout, ProLayoutProps } from '@ant-design/pro-layout'
import { CurrentOrganizationUser } from '@organization/api/requests'
import { BrokerApiSettings } from '@organization/features/broker-api-settings'
import { Tickets } from '@organization/features/tickets'
import { useWizardContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { LanguageSwitcherProps } from '@shared/types'
import { getFullName } from '@shared/utils'
import {
  Button,
  Divider,
  Dropdown,
  Flex,
  FloatButton,
  Popconfirm,
  Tour
} from 'antd'
import { MenuProps } from 'antd/lib'
import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { AppSelector } from '../Applications'
import { Header } from '../Header'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { useStyles } from './styles'

type Props = {
  hiddenMenuExtra?: boolean
  menu: MenuDataItem[]
  profileMenuItems: MenuProps['items']
  organizationData?: CurrentOrganizationUser
  brokerName?: React.ReactNode
  languages?: LanguageSwitcherProps
  showTitle?: boolean
  collapsedIcon?: ReactNode | string
  children: ReactNode
} & Pick<ProLayoutProps, 'menuExtraRender' | 'actionsRender'> & {
    selectedKeys?: string[]
  }

/**
 * **Note:** This component must be used inside a `BrowserRouter`.
 *
 * **Note:** This component is a wrapper around [Ant Design ProComponents ProLayout](https://procomponents.ant.design/components/layout#prolayout) and extends it's props.
 *
 * @param menu - An array of `MenuDataItem` objects from [Ant Design ProComponents](https://procomponents.ant.design/components/layout#menudataitem).
 *
 * Example:
 * ```tsx
 * const menu = [{
 *   key: 'home',
 *   name: 'Home',
 *   path: '/',
 *   icon: <Home />
 * }];
 * ```
 *
 * @param profileMenuItems - An array of items from [Ant Design MenuProps](https://ant.design/components/menu/#Menu-Props).
 *
 * Example:
 * ```tsx
 * const profileMenuItems = [{
 *   key: 'home',
 *   label: 'Home',
 *   onClick: () => console.log('Profile clicked')
 * }];
 * ```
 *
 * @param organizationData - Data of type [SessionData](../../types/layout.ts).
 *
 * @param languages - Data of type [LanguageSwitcherProps](../LanguageSwitcher/types.ts).
 *
 * @param children - The content to be rendered within the layout.
 *
 * @param showTitle - Whether to show the organization name in the header. Default is `true`.
 *
 * @param collapsedIcon - The icon to show when the sidebar is collapsed. Default is `null`. Uses the organization photo.
 *
 * @description This component represents the layout of the application, including the header, sidebar, and footer.
 *
 * @example
 * ```tsx
 * <Layout
 *   menu={menu}
 *   profileMenuItems={profileMenuItems}
 *   organizationData={organizationData}
 *   languages={languages}
 * >
 *   {children}
 * </Layout>
 * ```
 */

export const Layout = ({
  menu,
  profileMenuItems,
  organizationData,
  languages,
  collapsedIcon,
  children,
  menuExtraRender,
  actionsRender,
  hiddenMenuExtra,
  brokerName,
  ...rest
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false)

  const { t } = useLocales()

  const {
    open,
    steps,
    setOpen,
    refStepTwo,
    current,
    isLoading,
    onChangeStep,
    isBrokerAdmin
  } = useWizardContext()

  const location = useLocation()
  const { styles } = useStyles()

  // const { data: unreadNotifications } =
  //   useNotificationServiceNotificationItemsUnreadCountRetrieve({})

  const user = organizationData?.user

  const { selectedKeys, ...proLayoutProps } = rest

  return (
    <ProLayout
      logo={<img src={'/images/logo.svg'} alt="logo" className={styles.logo} />}
      title={organizationData?.organization?.name}
      siderWidth={250}
      layout="mix"
      className={styles.sider}
      location={location}
      headerTitleRender={(logo) => (
        <Header
          isCollapsed={isCollapsed}
          logo={isCollapsed && collapsedIcon ? collapsedIcon : logo}
          title={null}
        />
      )}
      onCollapse={() => setIsCollapsed(!isCollapsed)}
      menuExtraRender={({ collapsed }) =>
        hiddenMenuExtra ? (
          false
        ) : (
          <>
            <AppSelector
              isCollapsed={collapsed}
              organizationData={organizationData}
            />
            <Divider className={styles.divider} />

            {menuExtraRender && menuExtraRender({ collapsed })}
          </>
        )
      }
      menuDataRender={() => menu}
      menuItemRender={(menuItemProps, defaultDom) => {
        return (
          <Link
            to={menuItemProps.path ?? ''}
            ref={menuItemProps.wizard ? refStepTwo : null}
          >
            {defaultDom}
          </Link>
        )
      }}
      // menuItemRender={(menuItemProps, defaultDom) => {
      //   if (menuItemProps.key === 'notifications') {
      //     return (
      //       <Link to={menuItemProps.path ?? ''}>
      //         <Flex justify="space-between" align="center" className="w-full">
      //           {defaultDom}

      //           {!!unreadNotifications?.unread_count && (
      //             <Badge
      //               count={`+${unreadNotifications?.unread_count}`}
      //               overflowCount={999}
      //             />
      //           )}
      //         </Flex>
      //       </Link>
      //     )
      //   }
      //   return <Link to={menuItemProps.path ?? ''}>{defaultDom}</Link>
      // }}
      avatarProps={{
        src: user?.photo,
        size: 'small',
        title: (
          <Flex vertical>
            <span className={styles.accountDetails}>{getFullName(user)}</span>
            {!!brokerName && (
              <span className={styles.accountDetails}>{brokerName}</span>
            )}
          </Flex>
        ),
        render: (_, dom) => {
          return (
            <Dropdown
              menu={{
                items: profileMenuItems
              }}
            >
              {dom}
            </Dropdown>
          )
        }
      }}
      actionsRender={(...rest) => (
        <>
          {isBrokerAdmin && <BrokerApiSettings />}

          {import.meta.env.VITE_APP_ENV !== 'production' && <Tickets />}

          {actionsRender ? actionsRender(...rest) : null}
          {languages && (
            <LanguageSwitcher
              languages={languages?.languages}
              onLanguageChange={languages.onLanguageChange}
              currentLang={languages.currentLang}
            />
          )}
        </>
      )}
      selectedKeys={selectedKeys}
      {...proLayoutProps}
    >
      {children}

      <Tour
        closeIcon={false}
        open={open}
        current={current}
        onClose={() => setOpen(false)}
        steps={steps}
        gap={{ offset: [20, 5] }}
        actionsRender={(originNode) => (
          <>
            <Button
              size="small"
              loading={isLoading}
              onClick={() => {
                setOpen(false)
                onChangeStep(5)
              }}
            >
              {t('skip')}
            </Button>

            {originNode}
          </>
        )}
      />

      {isBrokerAdmin && (
        <Popconfirm
          placement="left"
          title={t('help_wizard')}
          description={t('help_wizard_description')}
          onConfirm={() => onChangeStep(0)}
          okText={t('yes')}
          cancelText={t('no')}
          zIndex={50}
        >
          <FloatButton
            type="primary"
            icon={<QuestionCircleOutlined />}
            tooltip={t('help_wizard')}
          />
        </Popconfirm>
      )}
    </ProLayout>
  )
}
